import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { selectPartner360Data } from 'legacy/lut/features/partner360/configuration'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useSafetyTranslation } from 'cng-common/features/translations'

import type { ICockpitExportContext, ICockpitExportFilter } from '../../api'
import { getCockpitFiscalYearLabel, getCockpitPeriodLabel, getOfferingTitleKey } from '../../lib'
import {
  getCockpitPartnerMarketShortName,
  getCockpitPartnerRegionLabel,
} from '../../ui/cockpitPartnerDetails/lib/getCockpitPartnerDetails'
import {
  selectCockpitCategory,
  selectCockpitCurrency,
  selectCockpitGeoMappingItems,
  selectCockpitMetric,
  selectCockpitOffering,
  selectCockpitPartnerData,
  selectCockpitPeriods,
  selectCockpitRtm,
  selectEffectiveCockpitFYear,
} from '../selectors'

import { useCockpitFilterOptionsBubbleItems } from './useCockpitFilterOptionsBubbleItems'
import { useCockpitPartnersShown } from './useCockpitPartnersShown'
import { useFilteredCockpitPartnerData } from './useFilteredCockpitPartnerData'

/** Fills the counts into the panel's own sentence the way `Translation` does, minus the markup
 *  the panel wraps them in — an export carries text, not HTML. */
const buildFilterSummary = (
  translate: (key: string) => string,
  { shownCount, totalCount, textKey }: ReturnType<typeof useCockpitPartnersShown>
) =>
  translate(textKey)
    .replaceAll('{count}', String(shownCount))
    .replaceAll('{totalCount}', String(totalCount))

/**
 * The scope and the filters every cockpit tile reads its numbers under — the part of an export
 * that is set outside the tile and is the same whichever tile is being exported.
 */
export const useCockpitExportContext = (isManagerCockpitMode = false): ICockpitExportContext => {
  const t = useSafetyTranslation()
  /** What the tile headers read with no offering selected — see `CockpitFilterSummary`. */
  const programTypeKey = useSelector(selectCockpitOffering) || ProgramTypes.Overall
  const category = useSelector(selectCockpitCategory)
  const periods = useSelector(selectCockpitPeriods)
  const metric = useSelector(selectCockpitMetric)
  const currency = useSelector(selectCockpitCurrency)
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const rtm = useSelector(selectCockpitRtm)
  /** The manager cockpit shows whatever its filters leave; the partner cockpit holds one
   *  partner, and reads it unfiltered — the RTM switch is a manager control. */
  const partnerData = useSelector(selectCockpitPartnerData)
  const filteredPartnerData = useFilteredCockpitPartnerData()
  const partners = isManagerCockpitMode ? filteredPartnerData : partnerData
  /** Only the manager cockpit has a filter panel; the partner cockpit sends neither of these. */
  const filterChips = useCockpitFilterOptionsBubbleItems()
  const partnersShown = useCockpitPartnersShown()
  /** The partner card's line under the name, composed as `CockpitPartnerDetails` composes it. */
  const { geoMarket, country } = useSelector(selectPartner360Data)
  const geoMappingItems = useSelector(selectCockpitGeoMappingItems)

  return useMemo(
    () => ({
      programType: t(getOfferingTitleKey(programTypeKey)),
      programTypeKey,
      category: category ? t(category) : null,
      fiscalYear: getCockpitFiscalYearLabel(fYear),
      period: getCockpitPeriodLabel(periods),
      periods,
      netNdp: metric,
      currency,
      ...(isManagerCockpitMode
        ? {
            filters: filterChips.map<ICockpitExportFilter>(({ label, value }) => ({
              label,
              value: value ?? '',
            })),
            filterSummary: buildFilterSummary(t, partnersShown),
          }
        : {}),
      scope: isManagerCockpitMode
        ? { isManagerCockpit: true, partnerCount: partners.length, rtm }
        : {
            isManagerCockpit: false,
            partnerCount: partners.length,
            partnerId: partners[0]?.PartnerId,
            partnerName: partners[0]?.PartnerName,
            /** Left out where the card shows `--`, as an optional field is absent rather than a
             *  placeholder. */
            partnerLocation:
              getCockpitPartnerRegionLabel(
                getCockpitPartnerMarketShortName(geoMappingItems, geoMarket),
                country
              ) || undefined,
          },
    }),
    [
      t,
      programTypeKey,
      category,
      fYear,
      periods,
      metric,
      currency,
      isManagerCockpitMode,
      partners,
      rtm,
      filterChips,
      partnersShown,
      geoMappingItems,
      geoMarket,
      country,
    ]
  )
}
